import { variantProvider, React } from '@/app'
import { TypeGuards } from '@codeleap/common'
import { ActivityIndicator, View, Text, Image, Icon, Touchable } from '@/components'
import {
  createContext,
  CSSProperties,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { globalState, GlobalState } from '@codeleap/store'
import { shadeColor } from '@codeleap/utils'
import { useReactPlayer } from './hooks/useReactPlayer'
import ReactPlayer, { ReactPlayerProps } from 'react-player'
import { useHover, useIsValidImage, useLazyStore } from '@/utils'
import { Imaging } from '@/services'
import { useVolume } from '@/utils'
import { SuccessOverlay } from '../SuccessOverlay'

const thumbnailIndex = 0
const backgroundIndex = -2

type LoadData = {
  ready: boolean
}

type ErrorData = {
  err: any
}

type VideoContextProps = {
  width?: number | string
  height?: number | string
  metadataStore?: GlobalState<LoadData>
  errorStore?: GlobalState<ErrorData>
  mute?: boolean
  volume?: number
  controller: ReturnType<typeof useImmersiveController>
}

type VideoContextValue = VideoContextProps & {
  progress: any
}

const VideoContext = createContext({} as VideoContextValue)

const useVideoContext = () => {
  return useContext(VideoContext)
}

const interactedStore = globalState(false)

const useImmersiveController = () => {
  const [playing, setPlaying] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const playerRef = useRef<ReactPlayer>(null)

  const pause = () => {
    if (!loaded) return
    setPlaying(false)
  }

  const resume = () => {
    if (!loaded) return
    setPlaying(true)
  }

  const toggle = () => {
    if (!loaded) return
    setPlaying(curr => !curr)
  }

  const onRefSet = (value: ReactPlayer) => {
    playerRef.current = value
  }

  return {
    ref: playerRef,
    onRefSet,
    pause,
    resume,
    toggle,
    playing,
    setLoaded,
    loaded
  }
}

const Error = () => {
  const { errorStore } = useVideoContext()

  const hasError = errorStore.use(s => !TypeGuards.isNil(s))

  return (
    <SuccessOverlay
      icon="video"
      visible={hasError}
      title="Sorry"
      description={`this video can't be reproduced`}
      style={{ zIndex: 3 }}
    />
  )
}

const Thumbnail = ({ thumbnail, style }) => {
  const isValid = useIsValidImage(thumbnail)

  if (!isValid) return null

  return (
    <Image
      // @ts-ignore
      css={[styles.absolute, styles.background, style]}
      source={thumbnail}
      objectFit="contain"
    />
  )
}

const Placeholder = (props: { thumbnail?: string; style?: CSSProperties }) => {
  const { thumbnail, style } = props
  const { metadataStore, errorStore } = useVideoContext()

  const isLoading = metadataStore.use(s => TypeGuards.isNil(s))
  const hasError = errorStore.use(s => !TypeGuards.isNil(s))

  if (!isLoading || hasError) return null

  return (
    <View css={[styles.absolute, styles.activityIndicatorWrapper]}>
      <Thumbnail thumbnail={thumbnail} style={style} />
      <ActivityIndicator debugName="" variants={['margin:auto']} style={{ zIndex: 10 }} />
    </View>
  )
}

const Controls = (props: { seekable?: boolean }) => {
  const { controller } = useVideoContext()

  const { isHovered, setIsHovered } = useHover(3000)
  const interacted = interactedStore.use()

  return (
    <>
      <View
        css={[styles.overlay, { opacity: isHovered || !interacted ? 1 : 0 }]}
        onMouseMove={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Touchable
          onPress={() => {
            controller.toggle()
            interactedStore.set(true as never)
          }}
          debugName="Player:PlayPause"
          variants={['flex']}
        >
          <Icon
            debugName="Player:Button"
            css={styles.playPause}
            variants={['white', 'huge']}
            name={controller.playing ? 'pause-circle-fill' : 'play-circle-fill'}
          />
        </Touchable>
      </View>
    </>
  )
}

const invisible = { width: 0, height: 0 }

const Video = forwardRef((props: ReactPlayerProps & { autoplay?: boolean; thumbnail: string }, ref) => {
  const { metadataStore, errorStore, height, width, controller, mute, volume } = useVideoContext()

  const ReactPlayer = useReactPlayer()

  const metadataDimension = Imaging.useImageSize(props?.thumbnail)

  const videoSize = useMemo(() => {
    const aspectRatio = metadataDimension?.data?.width / metadataDimension?.data?.height

    const isLandscape = metadataDimension?.data?.width > metadataDimension?.data?.height

    if (height && !isLandscape) {
      return {
        height,
        width: TypeGuards.isNumber(height) ? aspectRatio * height : `calc(${height} * ${aspectRatio}px)`
      }
    } else if (width) {
      return {
        height: TypeGuards.isNumber(width) ? width / aspectRatio : `calc(${width} / ${aspectRatio}px)`,
        width: width
      }
    }

    return invisible
  }, [metadataDimension?.data])

  const isLandscape = metadataDimension?.data?.width > metadataDimension?.data?.height

  const onLoad = useCallback(() => {
    setTimeout(() => {
      metadataStore.set({ ready: true })
      controller.setLoaded(true)
      errorStore.set(null)
    }, 3000)
  }, [])

  const onError = useCallback((error: any) => {
    console.log('Immersive video error', error)

    if (error !== 'hslError') {
      // errorStore.set(error)
    }
  }, [])

  const wrapperStyle = useMemo(() => {
    return {
      justifyContent: 'center',
      alignItems: 'center',
      width: width ?? '100%',
      height: height ?? '100%'
    }
  }, [width, height, isLandscape])

  const isBlob = typeof props?.url === 'string' && props?.url?.includes('blob')

  return (
    <View style={wrapperStyle}>
      <ReactPlayer
        controls={false}
        ref={ref}
        playing={controller.playing}
        loop
        {...props}
        playsinline={true}
        width={videoSize?.width}
        height={videoSize?.height}
        muted={mute}
        volume={volume}
        onError={onError}
        onReady={onLoad}
        onPlay={() => errorStore.set(null)}
        // config={isBlob ? undefined : {
        //   'file': {
        //     forceHLS: true,
        //     hlsOptions: {
        //       maxLoadingDelay: 10,
        //       maxRetryDelay: 5000,
        //       lowLatencyMode: false,
        //     },
        //   }
        // }}
      />
    </View>
  )
})

export const ImmersiveVideo = (_props: React.PropsWithChildren<VideoContextProps>) => {
  const { children, errorStore: _errorStore, metadataStore: _metadataStore, mute, volume, ...props } = _props

  const errorStore = _errorStore ?? useLazyStore<ErrorData>(null)
  const metadataStore = _metadataStore ?? useLazyStore<LoadData>(null)

  const value: VideoContextValue = { ...props, errorStore, metadataStore, progress: 0, mute, volume }

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>
}

/**
- https://developer.chrome.com/blog/autoplay/
*/
const useCaptureFirstInteraction = () => {
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (ImmersiveVideo.interactedStore.get()) return

    const handleFirstInteraction = event => {
      ImmersiveVideo.interactedStore.set(true as never)

      cleanupRef.current?.()
      cleanupRef.current = null
    }

    const events = [
      'click',
      'keydown',
      'keyup',
      'keypress',
      'scroll',
      'focus',
      'dragstart',
      'drag',
      'dblclick',
      'selectstart'
    ]

    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { once: true, passive: true })
    })

    cleanupRef.current = () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction)
      })
    }

    return () => {
      cleanupRef.current?.()
    }
  }, [])
}

ImmersiveVideo.Controls = Controls
ImmersiveVideo.Placeholder = Placeholder
ImmersiveVideo.Video = Video
ImmersiveVideo.Error = Error
ImmersiveVideo.use = useImmersiveController
ImmersiveVideo.interactedStore = interactedStore
ImmersiveVideo.useCaptureFirstInteraction = useCaptureFirstInteraction

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      borderRadius: 0,
      flexGrow: 1,
      width: '100%',
      height: '100%'
    },
    absolute: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    background: {
      zIndex: backgroundIndex
    },
    playButton: {
      backgroundColor: 'none'
    },
    activityIndicatorWrapper: {
      zIndex: thumbnailIndex,
      ...theme.presets.center,
      backgroundColor: shadeColor(theme.colors.neutral10, 0, 0.8)
    },
    player: {
      ...theme.spacing.marginHorizontal('auto'),
      overflow: 'hidden'
    },
    overlay: {
      zIndex: 2,
      ...theme.presets.absolute,
      ...theme.presets.fullHeight,
      ...theme.presets.fullWidth,
      ...theme.presets.justifyCenter,
      maxHeight: '100vh',
      overflow: 'hidden',
      top: 0,
      left: 0,
      background:
        'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.00) 0.01%, rgba(0, 0, 0, 0.90) 100%, rgba(0, 0, 0, 0.70) 100%)',
      transition: 'opacity 0.3s ease-in-out'
    },
    playPause: {
      ...theme.presets.absolute,
      ...theme.presets.alignSelfCenter
    }
  }),
  true
)
